import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef, useState, } from "react";
import { createPortal } from "react-dom";
/**
 * Click-to-open popover anchored below its trigger. Closes on outside click,
 * Escape and when `close()` is called from the content.
 *
 * The panel is rendered into document.body at a fixed position under the
 * trigger, so a header that clips its content (overflow, clip-path) does not
 * cut it off. Viewport-safe: it is clamped into the viewport horizontally (a
 * non-rightmost trigger, e.g. a notifications bell before the user menu, never
 * pushes it off the left edge on a narrow phone) and its height is capped with
 * internal scroll.
 */
export function Popover({ trigger, children, align = "right", width = 320, label, triggerClassName, open: openProp, onOpenChange, }) {
    const [openState, setOpenState] = useState(false);
    const open = openProp ?? openState;
    const setOpen = (next) => {
        if (openProp === undefined)
            setOpenState(next);
        onOpenChange?.(next);
    };
    const ref = useRef(null);
    const panelRef = useRef(null);
    const id = useId();
    // where the panel goes (viewport px), recomputed on open, resize and scroll
    const [place, setPlace] = useState();
    const shown = open && place !== undefined;
    // outside click and Escape close it, only while its panel is shown
    useEffect(() => {
        if (!shown)
            return;
        const onDown = (e) => {
            const target = e.target;
            if (ref.current?.contains(target) || panelRef.current?.contains(target))
                return;
            setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey, true);
        };
        // setOpen only forwards to the state / onOpenChange
    }, [shown]);
    // Under the trigger, aligned to its right (or left) edge, clamped into the
    // viewport; followed while open (resize, scroll).
    useLayoutEffect(() => {
        if (!open) {
            setPlace(undefined);
            return;
        }
        const measure = () => {
            const anchor = ref.current;
            // a trigger that is not displayed opens nothing
            if (!anchor || anchor.getClientRects().length === 0) {
                setPlace(undefined);
                return;
            }
            const margin = 8;
            const r = anchor.getBoundingClientRect();
            const vw = document.documentElement.clientWidth;
            const w = Math.min(width, vw - 2 * margin);
            const wanted = align === "right" ? r.right - w : r.left;
            const left = Math.min(Math.max(wanted, margin), vw - margin - w);
            const top = r.bottom + 8;
            setPlace({ top, left, width: w, maxHeight: Math.max(120, window.innerHeight - top - margin) });
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [open, align, width]);
    return (_jsxs("div", { ref: ref, className: "relative", children: [_jsx("button", { type: "button", "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": id, "aria-label": label, className: triggerClassName, onClick: () => setOpen(!open), children: trigger(open) }), shown &&
                createPortal(_jsx("div", { id: id, ref: panelRef, role: "dialog", "aria-label": label, className: "anim-pop scroll-thin fixed z-[60] flex flex-col overflow-y-auto rounded-2xl bg-elevated text-fg shadow-2xl ring-1 ring-line", style: place, children: children(() => setOpen(false)) }), document.body)] }));
}
